import { useState, useRef, useEffect } from "react";

interface Message {
  role: "user" | "bot";
  text: string;
}

const QUICK_REPLIES = [
  "Recommend me an RPG",
  "Best free-to-play games?",
  "Show me deals under $20",
  "How do I publish a game?",
];

const BOT_RESPONSES: Record<string, string> = {
  default: "I'm here to help you discover your next favourite game! Try asking me for genre recommendations or deals. 🎮",
  rpg: "For RPGs, I'd suggest **Starfield Chronicles** (4.8★) for epic space adventure, or **Dragon Siege** for a free-to-play fantasy MMO with deep guild mechanics!",
  free: "Our top free-to-play titles right now: **Cyber Drift** (racing), **Dragon Siege** (fantasy RPG), and **Arctic Storm** (battle royale). All available now, no credit card needed!",
  deal: "Hottest deals: Shadow Protocol is 25% off at $44.99, Iron Legion at 30% off, and Quantum Break: Origins at 40% off — all limited time offers!",
  publish: "To publish on GameStore: apply via the Publisher Portal, submit your game listing with screenshots + WebGL build, set your IAP items, and our team reviews within 48h. Click 'Start Publishing' to begin!",
};

function getResponse(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("rpg") || m.includes("role")) return BOT_RESPONSES.rpg;
  if (m.includes("free") || m.includes("f2p")) return BOT_RESPONSES.free;
  if (m.includes("deal") || m.includes("sale") || m.includes("cheap") || m.includes("discount")) return BOT_RESPONSES.deal;
  if (m.includes("publish") || m.includes("developer") || m.includes("submit")) return BOT_RESPONSES.publish;
  return BOT_RESPONSES.default;
}

export function ChatbotWidget() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "bot", text: "Hi there! 👋 I'm the GameStore assistant. Ask me anything about games, deals, or publishing!" },
  ]);
  const [input, setInput]       = useState("");
  const [typing, setTyping]     = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { role: "user", text };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { role: "bot", text: getResponse(text) }]);
    }, 900 + Math.random() * 400);
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 150,
          width: 54, height: 54, borderRadius: "50%",
          background: open ? "var(--bg-surface)" : "var(--accent)",
          border: `2px solid ${open ? "var(--border)" : "var(--accent-light)"}`,
          color: "#fff", fontSize: 22, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: open ? "none" : "0 4px 24px var(--accent-glow)",
          transition: "all var(--t-med)",
        }}
        title={open ? "Close chat" : "Chat support"}
      >
        {open ? "×" : "💬"}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: "fixed", bottom: 94, right: 28, zIndex: 150,
            width: 340, background: "var(--bg-surface)",
            border: "1px solid var(--border)", borderRadius: "var(--radius-xl)",
            boxShadow: "0 16px 60px rgba(0,0,0,0.5)",
            display: "flex", flexDirection: "column", overflow: "hidden",
            animation: "fadeUp 0.25s ease",
          }}
        >
          {/* Header */}
          <div style={{ background: "var(--accent)", padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🤖</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>GameStore Assistant</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--color-free)", display: "inline-block" }} />
                Online · Powered by AI (GS-901)
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 10, maxHeight: 340 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-end" }}>
                {m.role === "bot" && (
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--accent-subtle)", border: "1px solid var(--border-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🤖</div>
                )}
                <div
                  style={{
                    maxWidth: "78%", padding: "9px 13px", borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    background: m.role === "user" ? "var(--accent)" : "var(--bg-surface-2)",
                    border: m.role === "user" ? "none" : "1px solid var(--border)",
                    fontSize: 13, lineHeight: 1.6, color: m.role === "user" ? "#fff" : "var(--text-secondary)",
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--accent-subtle)", border: "1px solid var(--border-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🤖</div>
                <div style={{ padding: "9px 14px", borderRadius: "14px 14px 14px 4px", background: "var(--bg-surface-2)", border: "1px solid var(--border)", display: "flex", gap: 4, alignItems: "center" }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted)", display: "block", animation: `pulse 1.2s ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          <div style={{ padding: "6px 12px", display: "flex", gap: 6, overflowX: "auto" }}>
            {QUICK_REPLIES.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                style={{
                  flexShrink: 0, padding: "5px 11px", borderRadius: 20,
                  background: "var(--accent-subtle)", border: "1px solid var(--border-accent)",
                  color: "var(--text-accent)", fontSize: 11, fontWeight: 600,
                  cursor: "pointer", fontFamily: "var(--font-body)", whiteSpace: "nowrap",
                  transition: "all var(--t-fast)",
                }}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Input */}
          <div style={{ padding: "10px 12px 14px", borderTop: "1px solid var(--border)", display: "flex", gap: 8 }}>
            <input
              className="gs-input"
              style={{ flex: 1, height: 38, fontSize: 13 }}
              placeholder="Ask me anything…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send(input)}
            />
            <button
              onClick={() => send(input)}
              style={{
                width: 38, height: 38, borderRadius: "var(--radius-sm)",
                background: "var(--accent)", border: "none", color: "#fff",
                fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all var(--t-fast)",
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }
      `}</style>
    </>
  );
}
